import { collection, getCountFromServer, query, where, getDocs, doc, getDoc, orderBy, limit, startAfter, DocumentSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import { UserData } from '../types/user';
import { ActiveSubscription } from '../types/subscription';

export interface PaginatedUsersResult {
    users: (UserData & { uid: string })[];
    lastDoc: DocumentSnapshot | null;
    hasMore: boolean;
}

export interface SearchUsersResult {
    users: (UserData & { uid: string })[];
    total: number;
}

export interface UserSignupData {
    x: string; // week label
    y: number; // count for that week
}

export interface WeeklyUserData {
    weekStart: string;
    weekEnd: string;
    weekLabel: string;
    count: number;
}

export interface DailyUserData {
  date: string; // ISO date string (YYYY-MM-DD)
  dayLabel: string; // Human-readable label (e.g., "Mon, Jan 20")
  count: number;
}


export interface GetRecentUsersResponse {
    success: boolean;
    totalUsers: number;
    weeks: WeeklyUserData[];
    periodStart: string;
    periodEnd: string;
}

export interface DailyUserData {
    date: string;
    dayLabel: string;
    count: number;
}

export interface Get7DayTrendResponse {
    success: boolean;
    totalUsers: number;
    days: DailyUserData[];
    periodStart: string;
    periodEnd: string;
}

interface RecentUserInfo {
  uid: string;
  email: string | undefined;
  createdAt: string;
}

interface Get10MostRecentUsersResponse {
  success: boolean;
  users: RecentUserInfo[];
}


export interface OrganisationMemberPrivacySettings {
    wellnessLogs: boolean;      // Wellness logs (sleep, mood, readiness)
    femaleCycleLogs: boolean;   // Female cycle logs
    trainingLogs: boolean;      // Training sessions and activities
    niggleLogs: boolean;        // Injury and niggle tracking
    performanceLogs: boolean;   // Performance test results
    healthLogs: boolean;        // Health stats and metrics
    noteLogs: boolean;          // Personal notes and reflections
    recoveryLogs: boolean;      // Recovery sessions and metrics
    updatedAt?: Date;           // When settings were last modified
}

export interface OrganisationMember {
    userId: string;
    orgId: string;
    role: 'owner' | 'admin' | 'coach' | 'member' | 'medical' | 'athlete';
    joinedAt?: Date;
    invitedBy?: string;
    status: 'active' | 'pending' | 'suspended';
    team?: string | null;
    teams?: string[];
    profile?: any;
    // metrics?: AthleteMetrics;
    privacySettings?: OrganisationMemberPrivacySettings;  // Privacy settings for this member
}

export interface Organisation {
  id: string;
  name: string;
  createdAt?: Date;
  updatedAt?: Date;
  lastUpdated?: Date;
  ownerId?: string;
  inviteCode?: string;
  onboardingComplete?: boolean;
  adminActivityCatIDs?: string[];
  logoUrl?: string;
  imageUrl?: string;
  description?: string;
  teams?: string[];
  groups?: Record<string, string[]>;
  members: OrganisationMember[];
}





class AdminService {
    /**
     * Get total count of all users
     */
    async getUserCount(): Promise<number> {
        try {
            const usersRef = collection(db, 'users');
            const snapshot = await getCountFromServer(usersRef);
            return snapshot.data().count;
        } catch (error) {
            console.error('Error getting user count:', error);
            throw new Error('Failed to retrieve user count');
        }
    }

    /**
     * Get count of coaches (role = 'coach', role = 'Coach', or isCoach = true)
     */
    async getCoachCount(): Promise<number> {
        try {
            const usersRef = collection(db, 'users');

            // Run parallel queries for each condition
            const [roleLowerSnap, roleUpperSnap, isCoachSnap] = await Promise.all([
                getDocs(query(usersRef, where('role', '==', 'coach'))),
                getDocs(query(usersRef, where('role', '==', 'Coach'))),
                getDocs(query(usersRef, where('isCoach', '==', true))),
            ]);

            // Deduplicate by user ID
            const coachIds = new Set<string>();
            roleLowerSnap.docs.forEach(doc => coachIds.add(doc.id));
            roleUpperSnap.docs.forEach(doc => coachIds.add(doc.id));
            isCoachSnap.docs.forEach(doc => coachIds.add(doc.id));

            return coachIds.size;
        } catch (error) {
            console.error('Error getting coach count:', error);
            throw new Error('Failed to retrieve coach count');
        }
    }

    async get3MonthNewUserTrend(): Promise<UserSignupData[]> {
        const docRef = doc(db, 'adminStats', '3MonthNewUserTrend');
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) {
            throw new Error('No 3-month new user trend data found');
        }
        const result = snapshot.data() as GetRecentUsersResponse;

        return result.weeks.map(week => ({
            x: week.weekLabel,
            y: week.count
        }));
    }

    async get7DayNewUserTrend(): Promise<UserSignupData[]> {
        const docRef = doc(db, 'adminStats', '7DayNewUserTrend');
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) {
            throw new Error('No 7-day new user trend data found');
        }
        const result = snapshot.data() as Get7DayTrendResponse;

        return result.days.map(day => ({
            x: day.dayLabel,
            y: day.count
        }));
    }

    /**
     * Get most recent users with their display info (name, profile picture)
     */
    async getMostRecentUsers(limit: number = 10): Promise<UserData[]> {
        const docRef = doc(db, 'adminStats', '10MostRecentUsers');
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) {
            throw new Error('No most recent users data found');
        }

        const data = snapshot.data();
        const userList: RecentUserInfo[] = data.users || [];

        // Get users and limit to requested amount
        const recentUsers = userList.slice(0, limit);

        // Fetch user data for each UID
        const users = await Promise.all(
            recentUsers.map(async (recentUser) => {
                try {
                    // Get public data
                    const publicDoc = await getDoc(doc(db, 'users', recentUser.uid));
                    const publicData = publicDoc.exists() ? publicDoc.data() : {};

                    // Get private data
                    const privateDoc = await getDoc(doc(db, 'users', recentUser.uid, 'private', 'data'));
                    const privateData = privateDoc.exists() ? privateDoc.data() : {};

                    return {
                        uid: recentUser.uid,
                        email: recentUser.email,
                        ...publicData,
                        ...privateData,
                    } as UserData;
                } catch (error) {
                    console.error(`Error fetching user ${recentUser.uid}:`, error);
                    return null;
                }
            })
        );

        // Filter out any null results
        return users.filter((user): user is UserData => user !== null);
    }

    /**
     * Get paginated list of users
     */
    async getUsers(pageSize: number = 20, lastDocument?: DocumentSnapshot | null): Promise<PaginatedUsersResult> {
        try {
            const usersRef = collection(db, 'users');
            let q = query(usersRef, orderBy('updatedAt'), limit(pageSize + 1));

            if (lastDocument) {
                q = query(usersRef, orderBy('updatedAt'), startAfter(lastDocument), limit(pageSize + 1));
            }

            const snapshot = await getDocs(q);
            const docs = snapshot.docs;
            const hasMore = docs.length > pageSize;
            const usersToProcess = hasMore ? docs.slice(0, pageSize) : docs;
            const lastDoc = usersToProcess.length > 0 ? usersToProcess[usersToProcess.length - 1] : null;

            // Fetch full user data for each user
            const users = await Promise.all(
                usersToProcess.map(async (userDoc) => {
                    try {
                        const publicData = userDoc.data();
                        const [privateDoc, subscriptionDoc] = await Promise.all([
                            getDoc(doc(db, 'users', userDoc.id, 'private', 'data')),
                            getDoc(doc(db, 'activeSubscriptions', userDoc.id))
                        ]);
                        const privateData = privateDoc.exists() ? privateDoc.data() : {};
                        const subscription = subscriptionDoc.exists() ? subscriptionDoc.data() : null;

                        return {
                            uid: userDoc.id,
                            ...publicData,
                            ...privateData,
                            subscription,
                        } as UserData & { uid: string; subscription?: ActiveSubscription };
                    } catch (error) {
                        console.error(`Error fetching user ${userDoc.id}:`, error);
                        return null;
                    }
                })
            );

            return {
                users: users.filter((user): user is UserData & { uid: string } => user !== null),
                lastDoc,
                hasMore,
            };
        } catch (error) {
            console.error('Error getting users:', error);
            throw new Error('Failed to retrieve users');
        }
    }

    /**
     * Search for a user by UID (exact match)
     */
    async searchUserByUid(uid: string): Promise<(UserData & { uid: string }) | null> {
        try {
            const publicDoc = await getDoc(doc(db, 'users', uid));
            if (!publicDoc.exists()) {
                return null;
            }

            const publicData = publicDoc.data();
            const [privateDoc, subscriptionDoc] = await Promise.all([
                getDoc(doc(db, 'users', uid, 'private', 'data')),
                getDoc(doc(db, 'activeSubscriptions', uid))
            ]);
            const privateData = privateDoc.exists() ? privateDoc.data() : {};
            const subscription = subscriptionDoc.exists() ? subscriptionDoc.data() : null;

            return {
                uid,
                ...publicData,
                ...privateData,
                subscription,
            } as UserData & { uid: string };
        } catch (error) {
            console.error('Error searching user by UID:', error);
            return null;
        }
    }

    /**
     * Search users by username (prefix match) - queries Firestore directly
     */
    async searchByUsername(searchTerm: string): Promise<(UserData & { uid: string })[]> {
        try {
            const usersRef = collection(db, 'users');
            const q = query(
                usersRef,
                where('username', '>=', searchTerm.toLowerCase()),
                where('username', '<=', searchTerm.toLowerCase() + '\uf8ff'),
                limit(50)
            );

            const snapshot = await getDocs(q);

            const users = await Promise.all(
                snapshot.docs.map(async (userDoc) => {
                    try {
                        const publicData = userDoc.data();
                        const [privateDoc, subscriptionDoc] = await Promise.all([
                            getDoc(doc(db, 'users', userDoc.id, 'private', 'data')),
                            getDoc(doc(db, 'activeSubscriptions', userDoc.id))
                        ]);
                        const privateData = privateDoc.exists() ? privateDoc.data() : {};
                        const subscription = subscriptionDoc.exists() ? subscriptionDoc.data() : null;

                        return {
                            uid: userDoc.id,
                            ...publicData,
                            ...privateData,
                            subscription,
                        } as UserData & { uid: string };
                    } catch (error) {
                        console.error(`Error fetching user ${userDoc.id}:`, error);
                        return null;
                    }
                })
            );

            return users.filter((user): user is UserData & { uid: string } => user !== null);
        } catch (error) {
            console.error('Error searching by username:', error);
            return [];
        }
    }

    /**
     * Search users by email using Cloud Function (Firebase Admin SDK)
     */
    async searchByEmail(searchTerm: string): Promise<(UserData & { uid: string })[]> {
        try {
            const searchUsersFunc = httpsCallable<{ searchTerm: string }, { users: RecentUserInfo[] }>(
                functions,
                'searchUsersViaAdmin'
            );
            const result = await searchUsersFunc({ searchTerm });

            const users = await Promise.all(
                result.data.users.map(async (userInfo) => {
                    try {
                        const publicDoc = await getDoc(doc(db, 'users', userInfo.uid));
                        const publicData = publicDoc.exists() ? publicDoc.data() : {};
                        const [privateDoc, subscriptionDoc] = await Promise.all([
                            getDoc(doc(db, 'users', userInfo.uid, 'private', 'data')),
                            getDoc(doc(db, 'activeSubscriptions', userInfo.uid))
                        ]);
                        const privateData = privateDoc.exists() ? privateDoc.data() : {};
                        const subscription = subscriptionDoc.exists() ? subscriptionDoc.data() : null;

                        return {
                            uid: userInfo.uid,
                            email: userInfo.email,
                            ...publicData,
                            ...privateData,
                            subscription,
                        } as UserData & { uid: string };
                    } catch (error) {
                        console.error(`Error fetching user ${userInfo.uid}:`, error);
                        return null;
                    }
                })
            );

            return users.filter((user): user is UserData & { uid: string } => user !== null);
        } catch (error) {
            console.error('Error searching by email:', error);
            return [];
        }
    }

    /**
     * Search users by email or username
     * Combines results from both username (Firestore) and email (Cloud Function) searches
     */
    async searchUsers(searchTerm: string): Promise<SearchUsersResult> {
        try {
            // Run both searches in parallel
            const [usernameResults, emailResults] = await Promise.all([
                this.searchByUsername(searchTerm),
                this.searchByEmail(searchTerm),
            ]);

            // Combine and deduplicate by UID
            const seenUids = new Set<string>();
            const combinedUsers: (UserData & { uid: string })[] = [];

            for (const user of [...usernameResults, ...emailResults]) {
                if (!seenUids.has(user.uid)) {
                    seenUids.add(user.uid);
                    combinedUsers.push(user);
                }
            }

            return {
                users: combinedUsers.slice(0, 50),
                total: combinedUsers.length,
            };
        } catch (error) {
            console.error('Error searching users:', error);
            throw new Error('Failed to search users');
        }
    }

    /**
     * Check if a user is a coach based on multiple criteria
     */
    private isCoach(user: UserData & { uid: string }): boolean {
        return (
            user.role === 'coach' ||
            user.role === 'Coach' ||
            (user as any).isCoach === true ||
            (user.organisationId !== null && user.organisationId !== undefined && user.organisationId !== '')
        );
    }

    /**
     * Get paginated list of coaches
     * Coaches are users with role = 'coach'/'Coach', isCoach = true, or organisationId != null
     */
    async getCoaches(pageSize: number = 20, lastDocument?: DocumentSnapshot | null): Promise<PaginatedUsersResult> {
        try {
            const usersRef = collection(db, 'users');

            // Run parallel queries for each coach condition
            const [roleLowerSnap, roleUpperSnap, isCoachSnap, orgIdSnap] = await Promise.all([
                getDocs(query(usersRef, where('role', '==', 'coach'))),
                getDocs(query(usersRef, where('role', '==', 'Coach'))),
                getDocs(query(usersRef, where('isCoach', '==', true))),
                getDocs(query(usersRef, where('organisationId', '!=', null))),
            ]);

            // Deduplicate by user ID and collect all coach docs
            const coachDocsMap = new Map<string, any>();

            for (const docSnap of roleLowerSnap.docs) {
                coachDocsMap.set(docSnap.id, docSnap);
            }
            for (const docSnap of roleUpperSnap.docs) {
                coachDocsMap.set(docSnap.id, docSnap);
            }
            for (const docSnap of isCoachSnap.docs) {
                coachDocsMap.set(docSnap.id, docSnap);
            }
            for (const docSnap of orgIdSnap.docs) {
                coachDocsMap.set(docSnap.id, docSnap);
            }

            // Convert to array and sort: organisationId first, then by username
            const allCoachDocs = Array.from(coachDocsMap.values());
            allCoachDocs.sort((a, b) => {
                const dataA = a.data();
                const dataB = b.data();
                const hasOrgA = dataA?.organisationId ? 1 : 0;
                const hasOrgB = dataB?.organisationId ? 1 : 0;

                // Sort by organisationId presence first (those with org come first)
                if (hasOrgB !== hasOrgA) {
                    return hasOrgB - hasOrgA;
                }

                // Then sort by username
                const usernameA = dataA?.username || '';
                const usernameB = dataB?.username || '';
                return usernameA.localeCompare(usernameB);
            });

            // Find starting index for pagination
            let startIndex = 0;
            if (lastDocument) {
                const lastId = lastDocument.id;
                const foundIndex = allCoachDocs.findIndex(d => d.id === lastId);
                if (foundIndex !== -1) {
                    startIndex = foundIndex + 1;
                }
            }

            // Get the page of docs
            const pageDocs = allCoachDocs.slice(startIndex, startIndex + pageSize);
            const hasMore = startIndex + pageSize < allCoachDocs.length;
            const lastDoc = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;

            // Fetch full user data for each coach
            const coaches = await Promise.all(
                pageDocs.map(async (coachDoc) => {
                    try {
                        const publicData = coachDoc.data();
                        const privateDoc = await getDoc(doc(db, 'users', coachDoc.id, 'private', 'data'));
                        const privateData = privateDoc.exists() ? privateDoc.data() : {};

                        return {
                            uid: coachDoc.id,
                            ...publicData,
                            ...privateData,
                        } as UserData & { uid: string };
                    } catch (error) {
                        console.error(`Error fetching coach ${coachDoc.id}:`, error);
                        return null;
                    }
                })
            );

            return {
                users: coaches.filter((coach): coach is UserData & { uid: string } => coach !== null),
                lastDoc,
                hasMore,
            };
        } catch (error) {
            console.error('Error getting coaches:', error);
            throw new Error('Failed to retrieve coaches');
        }
    }

    /**
     * Search coaches by username or email
     * Filters search results to only include coaches
     */
    async searchCoaches(searchTerm: string): Promise<SearchUsersResult> {
        try {
            // Get all search results first
            const allResults = await this.searchUsers(searchTerm);

            // Filter to only coaches
            const coachResults = allResults.users.filter(user => this.isCoach(user));

            return {
                users: coachResults,
                total: coachResults.length,
            };
        } catch (error) {
            console.error('Error searching coaches:', error);
            throw new Error('Failed to search coaches');
        }
    }

    /**
     * Search for a coach by UID (exact match)
     * Returns the user only if they are a coach
     */
    async searchCoachByUid(uid: string): Promise<(UserData & { uid: string }) | null> {
        try {
            const user = await this.searchUserByUid(uid);
            if (user && this.isCoach(user)) {
                return user;
            }
            return null;
        } catch (error) {
            console.error('Error searching coach by UID:', error);
            return null;
        }
    }

    async fetchOrganisations(): Promise<Organisation[]> {

        try {
            const orgsCollection = collection(db, 'organisations');
            const orgsSnapshot = await getDocs(orgsCollection);

            const BATCH_SIZE = 500;
            const orgIds = orgsSnapshot.docs.map(doc => doc.id);
            const organisationsList: Organisation[] = [];

            for (let i = 0; i < orgIds.length; i += BATCH_SIZE) {
                const batch = orgIds.slice(i, i + BATCH_SIZE);

                const orgPromises = batch.map(async (orgId) => {
                try {
                    const orgDocRef = doc(db, 'organisations', orgId);
                    const orgDoc = await getDoc(orgDocRef);

                    if (!orgDoc.exists()) {
                    return null;
                    }

                    const orgData = orgDoc.data();

                    // Fetch members for this organization
                    const membersCollection = collection(db, 'organisations', orgId, 'members');
                    const membersSnapshot = await getDocs(membersCollection);

                    const members: OrganisationMember[] = membersSnapshot.docs.map(memberDoc => {
                    const memberData = memberDoc.data();
                    const roleFromDoc = memberData.role;
                    const role: OrganisationMember['role'] =
                        roleFromDoc === 'owner' || roleFromDoc === 'admin' || roleFromDoc === 'coach' || roleFromDoc === 'member' || roleFromDoc === 'medical'
                            ? roleFromDoc
                            : roleFromDoc === 'athlete'
                                ? 'member'
                                : 'member';
                    const status = (memberData.status ?? 'active') as OrganisationMember['status'];
                    return {
                        userId: memberDoc.id,
                        orgId,
                        role,
                        joinedAt: memberData.joinedAt?.toDate?.() || undefined,
                        invitedBy: memberData.invitedBy,
                        status,
                        team: memberData.team ?? null,
                        teams: Array.isArray(memberData.teams) ? memberData.teams : undefined,
                        profile: memberData.profile,
                        privacySettings: memberData.privacySettings,
                    };
                    });

                    return {
                    id: orgId,
                    name: orgData.name || 'Unknown Organization',
                    createdAt: orgData.createdAt?.toDate?.() || undefined,
                    updatedAt: orgData.updatedAt?.toDate?.() || undefined,
                    lastUpdated: orgData.lastUpdated?.toDate?.() || undefined,
                    ownerId: orgData.ownerId,
                    inviteCode: orgData.inviteCode,
                    onboardingComplete: orgData.onboardingComplete,
                    adminActivityCatIDs: Array.isArray(orgData.adminActivityCatIDs) ? orgData.adminActivityCatIDs : undefined,
                    logoUrl: orgData.logoUrl,
                    imageUrl: orgData.imageUrl,
                    description: orgData.description,
                    teams: Array.isArray(orgData.teams) ? orgData.teams : undefined,
                    groups: orgData.groups && typeof orgData.groups === 'object'
                        ? orgData.groups as Record<string, string[]>
                        : undefined,
                    members,
                    } as Organisation;

                } catch (error) {
                    console.warn(`[COACHES_MANAGEMENT] Error fetching organization ${orgId}:`, error);
                    return null;
                }
                });

                const results = await Promise.all(orgPromises);
                organisationsList.push(...results.filter((org): org is Organisation => org !== null));
            }

            console.log(`[COACHES_MANAGEMENT] Successfully fetched ${organisationsList.length} organisations with member data`);
            return organisationsList;

        } catch (error) {
            console.error('[COACHES_MANAGEMENT] Error fetching organisations:', error);
            return [];
        }
    };
}

export const adminService = new AdminService();
